import Backoffice from "@snark/backoffice"
import Vue from "vue"
import projectorBoxes from "@/assets/projectorBoxes.json";

const context = {
	account: Backoffice.populator("account", {}),
	building: Backoffice.populator("building", {}),
	projector: Backoffice.populator("projector", {}),
};

function getBrandOptions() {
	return Object.keys(projectorBoxes).map(b => ({ value: b, label: b }));
}

function getModelOptions(brand) {
	return (projectorBoxes[brand] || []).map(m => ({ value: m.model, label: m.model }));
}

async function getAccountOptions() {
	const accounts = await Vue.backoffice.requestor.get("/account");
	return (Array.isArray(accounts) ? accounts : [])
		.map(account => ({ value: account._id, label: account.name || account._id }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

async function getBuildingOptions(input) {
	const accountId = typeof input === "string" ? input : input?.object?.accountId;
	if (!accountId) return [];

	const buildings = await Vue.backoffice.requestor.get("/building", null, { accountId });
	return (Array.isArray(buildings) ? buildings : [])
		.filter(building => building.accountId?.toString?.() === accountId.toString())
		.map(building => ({ value: building._id, label: building.name || building._id }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

async function getProjectorOptions(input) {
	const buildingId = typeof input === "string" ? input : input?.object?.buildingId;
	if (!buildingId) return [];

	const projectors = await Vue.backoffice.requestor.get("/projector", null, { buildingId });
	return (Array.isArray(projectors) ? projectors : [])
		.filter(projector => projector.type === "projector" && projector.buildingId?.toString?.() === buildingId.toString())
		.map(projector => ({ value: projector._id, label: projector.name || projector._id }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

function cleanItem(item) {
	const clean = { ...item, type: "projectorBox" };
	delete clean.account;
	delete clean.building;
	delete clean.projector;
	delete clean.projectorStatus;
	delete clean.vlbox;
	delete clean.lastSeenAt;
	delete clean.statusGraceMs;
	delete clean.createdAt;
	delete clean.updatedAt;
	return clean;
}

export default {
	name: "projector-box",
	collection: "projector",
	idProperty: "_id",
	section: "devices",
	backPopulateList: ["account", "building", "projector"],
	filterList: function(items) {
		return Array.isArray(items) ? items.filter(item => item.type === "projectorBox") : items;
	},
	labels: { singleTitle: "Caisson VP", listTitle: "Caissons VP" },
	emptyItem: { type: "projectorBox", accountId: null, buildingId: null, projectorId: null, projectorRefs: [], name: "", addressIP: "", adminInterface: "", brand: Object.keys(projectorBoxes)[0], model: getModelOptions(Object.keys(projectorBoxes)[0])[0]?.value },
	fields: [
		{ name: "_id", label: "Id", readOnly: true, hideOnCreate: true },
		{ name: "type", label: "Type", readOnly: true, formatter: Backoffice.formatters.mapping({ projectorBox: "Caisson VP" }) },
		{ name: "name", label: "Nom" },
		{
			name: "accountId",
			label: "Account",
			formatter: Backoffice.formatters.linkObjectId("account", { idProperty: "_id", label: context.account.formatter() }),
			editor: {
				type: "select",
				options: getAccountOptions,
				transform: (value, form) => {
					if (form.accountId !== value) {
						form.buildingId = null;
						form.projectorId = null;
					}
					return value;
				},
			},
		},
		{
			name: "buildingId",
			label: "Building",
			formatter: Backoffice.formatters.linkObjectId("building", { idProperty: "_id", label: context.building.formatter() }),
			editor: {
				type: "select",
				options: getBuildingOptions,
				watch: "accountId",
				transform: (value, form) => {
					if (form.buildingId !== value) form.projectorId = null;
					return value;
				},
			},
		},
		{
			name: "projectorId",
			label: "Projecteur principal (héritage)",
			formatter: Backoffice.formatters.linkObjectId("projector", { idProperty: "_id", label: context.projector.formatter() }),
			editor: {
				type: "select",
				options: getProjectorOptions,
				watch: "buildingId",
			},
		},
		{
			name: "projectorRefs",
			label: "Projecteurs associés",
			list: true,
			children: [
				{
					name: "projectorId",
					label: "Projecteur",
					formatter: Backoffice.formatters.linkObjectId("projector", { idProperty: "_id", label: context.projector.formatter() }),
					editor: {
						type: "select",
						options: getProjectorOptions,
						watch: "buildingId",
					},
				}
			]
		},
		{ name: "addressIP", label: "IP" },
		{ name: "adminInterface", label: "Interface d'administration" },
		{
			name: "brand",
			label: "Fabricant",
			editor: {
				type: "select",
				options: () => getBrandOptions(),
			},
		},
		{
			name: "model",
			label: "Modèle",
			editor: {
				type: "select",
				options: (input) => {
					const brand = typeof input === "string"
						? input
						: input?.object?.brand;

					return brand ? getModelOptions(brand) : [];
				},
				watch: "brand",
			}
		},
	],
	listColumns: [
		{ name: "name", label: "Nom" },
		{ name: "account", label: "Account", formatter: Backoffice.formatters.subProperty("name"), nosingle: true },
		{ name: "building", label: "Building", formatter: Backoffice.formatters.subProperty("name"), nosingle: true },
		{ name: "projector", label: "Projector", formatter: Backoffice.formatters.subProperty("name"), nosingle: true },
		{ name: "addressIP", label: "IP" },
		{ name: "adminInterface", label: "Interface d'administration" },
		{ name: "brand", label: "Fabricant" },
		{ name: "model", label: "Modèle" },
	],
	loadSingle() {
		return Promise.all([context.account.load(), context.building.load(), context.projector.load()]);
	},
	beforeSave(item) {
		return Promise.resolve(cleanItem(item));
	}
};
